// Shapes and queries the practitioner screens share. Source: docs/04-architecture/api-contract.md §6.
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { queryKeys } from "@/lib/query-keys";

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export interface Citation {
  sourceId: string;
  title: string;
  url: string;
  checkedOn: string;
}

export interface Warning {
  code: string;
  severity: "info" | "warning";
  message: string;
  requirementId?: string;
  confidence?: "high" | "medium" | "low";
  citation?: Citation;
  scope?: { from: string; to: string };
}

export interface Track {
  id: string;
  kind: "state_ruleset" | "custom";
  jurisdiction: string;
  pathway: string;
  credentialName: string;
  rulesetId: string | null;
  pinnedRulesetVersion: { id: string; label: string } | null;
  newerRulesetVersionAvailable: boolean;
  /** A custom track's own targets, plus the board the practitioner named. Null on a state track. */
  customTargets?: unknown;
  startedOn: string;
  status: "active" | "archived";
  verifiedStateSupport: boolean;
  /** Stamped when the licence is archived, cleared when it is reopened. */
  archivedAt?: string | null;
  /** When the state registered you, and when that registration runs out (FL counts from it). */
  registrationDate?: string | null;
  registrationExpiresOn?: string | null;
  version: number;
}

export interface ProgressBucket {
  bucket: string;
  label: string;
  recordedMinutes: number;
  creditedMinutes: number;
  targetMinutes: number | null;
  displayHours: string | null;
  displayTargetHours: string | null;
  approvedMinutes: number;
  pendingMinutes: number;
  /** Minutes still counted once the state's validity rules have run. Equal to recorded when
   * nothing voids anything, which is the usual case. */
  validMinutes?: number;
}

export interface Progress {
  asOf: string;
  rulesetVersion: { id: string; label: string } | null;
  verifiedStateSupport: boolean;
  /** Recorded vs valid: which recorded minutes a board may not count, and why. */
  validity?: {
    recordedMinutes: number;
    validMinutes: number;
    voids: {
      /** "2026-05", a date range, or one entry's date. */
      periodLabel: string;
      reason: string;
      citation: string | null;
      minutes: number;
      /** true = the minutes still count today; the board may still ask about them. */
      informational: boolean;
      entryIds: string[];
    }[];
    partitions: {
      siteId: string | null;
      supervisorRelationshipId: string | null;
      recordedMinutes: number;
      directMinutes: number;
      supervisionMinutes: number;
    }[];
  };
  buckets: ProgressBucket[];
  periodChecks: { periodStart: string; periodEnd: string; status: string; warnings: Warning[] }[];
  warnings: Warning[];
  disclaimer: string;
}

export interface Site {
  id: string;
  name: string;
  settingType: string;
  city?: string | null;
  state?: string | null;
  startedOn: string;
  closedOn?: string | null;
  version: number;
}

export interface SupervisorRow {
  relationshipId: string;
  /** `userId` is null until the supervisor has proved the email and has an account here. */
  supervisor: { userId: string | null; name: string | null; email: string | null };
  /** The one name every screen shows: their account name, or the name you typed. */
  displayName: string;
  email: string | null;
  /**
   * "unverified" means you added them yourself and they have not confirmed the email yet.
   * They are fully usable on entries and periods; only signing waits for the confirmation.
   */
  verification: "verified" | "unverified";
  verifiedAt: string | null;
  license: { state: string; type: string; number: string; expiresOn?: string | null } | null;
  site: { id: string; name: string } | null;
  status: "active" | "ended";
  startedOn: string;
  endedOn: string | null;
  /** WA: the date the supervision declaration was signed. FL: the date the board approved them. */
  declarationSignedOn?: string | null;
  boardApprovedOn?: string | null;
  version: number;
}

export interface Invitation {
  id: string;
  invitedEmail: string;
  status: "pending" | "accepted" | "revoked" | "declined" | "expired";
  expiresAt: string;
  sendCount: number;
  lastSentAt: string | null;
  emailDelivery: "queued" | "sent" | "failed" | "dead";
  version?: number;
}

export interface Entry {
  id: string;
  trackId: string;
  relationshipId: string | null;
  siteId: string | null;
  reportingPeriodId: string | null;
  importJobId: string | null;
  locked: boolean;
  entryMode: "session" | "period_total";
  serviceDate: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  category: string;
  minutes: number;
  displayHours: string;
  modality: "in_person" | "virtual" | "phone" | "async" | null;
  supervisionFormat: "individual" | "triadic" | "group" | "peer" | null;
  /** One of the ACTIVITY_TYPES ids (B1). Descriptive; the category still does the scoring. */
  activityType?: string | null;
  /** How many clients were in the session (B3). */
  clientCount?: number | null;
  /** More than one client in the room. The API derives it from the activity type. */
  relational?: boolean | null;
  /** How many meetings one supervision entry covers (S1). */
  supervisionMeetings?: number | null;
  /** "historical" when the entry's first date is before the track start (FR-LOG-010). */
  source?: "manual" | "import" | "historical" | null;
  /** The licence the supervisor held the day this was logged. The API fills it in on save. */
  supervisorLicenseTypeAtTime?: string | null;
  groupSize: number | null;
  notes: string | null;
  version: number;
}

export interface PeriodTotalsBucket {
  bucket: string;
  label: string;
  minutes: number;
  displayHours: string;
}

export interface Period {
  id: string;
  trackId: string;
  relationshipId: string;
  periodKind: "week" | "month" | "six_months" | "custom";
  startDate: string;
  endDate: string;
  state: "draft" | "submitted" | "returned" | "approved" | "withdrawn" | "amending";
  currentSubmissionId: string | null;
  /** When the current submission went to the supervisor. Drives the "send a reminder" nudge. */
  submittedAt?: string | null;
  /** The last time the supervisor was chased, stored, so a reload still shows the trail. */
  lastRemindedAt: string | null;
  /** How many reminders this period has had, counting the automatic 7 and 14 day ones. */
  reminderCount: number;
  entryCount: number;
  totals: PeriodTotalsBucket[];
  warnings: Warning[];
  canSubmit: boolean;
  /** True only on a signed period: the one kind that can be corrected. */
  canAmend: boolean;
  /** Set while a correction is open, and shown back to the practitioner. */
  amendmentReason?: string | null;
  blockers: string[];
  returnReason?: string | null;
  version: number;
}

export interface RulesetOption {
  id: string;
  key: string;
  jurisdiction: string;
  pathway: string;
  credentialNames: string[];
  currentVersion: {
    id: string;
    label: string;
    effectiveFrom: string;
    verificationStatus: string;
    sourcesCheckedOn: string;
  } | null;
  signatureNote: string;
}

export interface RulesetCatalog {
  items: RulesetOption[];
  customTracker?: { label: string };
}

/** Entries are read through this everywhere, so a filter change never misses the cache key. */
export function entryQueryString(filters: Record<string, string>): string {
  const params = new URLSearchParams(filters);
  const query = params.toString();
  return query ? `?${query}` : "";
}

export function useTracks() {
  return useQuery({
    queryKey: queryKeys.tracks(),
    queryFn: ({ signal }) => apiFetch<{ items: Track[] }>("/api/tracks", { signal }),
  });
}

export function useTrack(trackId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.track(trackId ?? ""),
    enabled: Boolean(trackId),
    queryFn: ({ signal }) => apiFetch<Track>(`/api/tracks/${trackId ?? ""}`, { signal }),
  });
}

export function useProgress(trackId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.progress(trackId ?? ""),
    enabled: Boolean(trackId),
    queryFn: ({ signal }) =>
      apiFetch<Progress>(`/api/tracks/${trackId ?? ""}/progress`, { signal }),
  });
}

export function useSites(trackId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sites(trackId ?? ""),
    enabled: Boolean(trackId),
    queryFn: ({ signal }) =>
      apiFetch<{ items: Site[] }>(`/api/tracks/${trackId ?? ""}/sites`, { signal }),
  });
}

export function useSupervisors(trackId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.relationships(trackId ?? ""),
    enabled: Boolean(trackId),
    queryFn: ({ signal }) =>
      apiFetch<{ items: SupervisorRow[] }>(`/api/tracks/${trackId ?? ""}/supervisors`, { signal }),
  });
}

export function useInvitations(trackId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.invitations(trackId ?? ""),
    enabled: Boolean(trackId),
    queryFn: ({ signal }) =>
      apiFetch<{ items: Invitation[] }>(`/api/tracks/${trackId ?? ""}/invitations`, { signal }),
  });
}

export function useEntries(trackId: string | undefined, filters: Record<string, string>) {
  return useQuery({
    queryKey: queryKeys.entries(trackId ?? "", filters),
    enabled: Boolean(trackId),
    queryFn: ({ signal }) =>
      apiFetch<Page<Entry>>(`/api/tracks/${trackId ?? ""}/entries${entryQueryString(filters)}`, {
        signal,
      }),
  });
}

export function useEntry(entryId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.entry(entryId ?? ""),
    enabled: Boolean(entryId),
    queryFn: ({ signal }) => apiFetch<Entry>(`/api/entries/${entryId ?? ""}`, { signal }),
  });
}

export function usePeriods(trackId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.periods(trackId ?? ""),
    enabled: Boolean(trackId),
    queryFn: ({ signal }) =>
      apiFetch<Page<Period>>(`/api/tracks/${trackId ?? ""}/periods`, { signal }),
  });
}

export function usePeriod(periodId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.period(periodId ?? ""),
    enabled: Boolean(periodId),
    queryFn: ({ signal }) =>
      apiFetch<Period & { entries: Entry[] }>(`/api/periods/${periodId ?? ""}`, { signal }),
  });
}

export function useRulesets() {
  return useQuery({
    queryKey: ["rulesets"],
    queryFn: ({ signal }) => apiFetch<RulesetCatalog>("/api/rulesets", { signal }),
  });
}
